import { toast } from "@suraasa/placebo-ui"
import {
  Auth,
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth"
import { app } from "firebaseInit"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"

export const getFirebaseAuth = (): Auth => {
  return getAuth(app)
}

export const getCleverRedirectUri = () =>
  `${window.location.origin}${routes.cleverAccountLinkCallback}`

const generateOAuthState = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("")
}

/**
 * Clever has no Firebase provider, so unlike Google/Facebook/Apple this can't
 * go through signInWithPopup — it's a full-page OAuth2 authorization-code
 * redirect. `state` (CSRF) is persisted to sessionStorage first since the
 * page navigates away; the callback validates it against what Clever sends
 * back.
 */
export const redirectToClever = () => {
  const clientId = import.meta.env.VITE_CLEVER_CLIENT_ID
  if (!clientId) {
    toast.error("Clever sign-in is not configured for this environment.")
    return
  }

  const state = generateOAuthState()
  sessionStorage.setItem(BROWSER_STORAGE_KEYS.cleverOAuthState, state)

  const url = new URL("https://clever.com/oauth/authorize")
  url.searchParams.set("response_type", "code")
  url.searchParams.set("client_id", clientId)
  url.searchParams.set("district_id", "6a4dd778aa1ec91f5083b56a") // TODO: This needs to be removed after testing
  url.searchParams.set("redirect_uri", getCleverRedirectUri())
  url.searchParams.set("state", state)

  window.location.href = url.toString()
}

type Options = {
  withDriveScope?: boolean
  email?: string
}

export const googleAuthHandler = async (options?: Options) => {
  const provider = new GoogleAuthProvider()
  provider.addScope("https://www.googleapis.com/auth/userinfo.email")
  provider.addScope("https://www.googleapis.com/auth/userinfo.profile")
  provider.addScope("openid")
  if (options?.withDriveScope) {
    provider.addScope("https://www.googleapis.com/auth/drive.file")
  }

  if (options?.email) {
    provider.setCustomParameters({ user_id: options.email })
  }

  const auth = getFirebaseAuth()

  try {
    const result = await signInWithPopup(auth, provider)
    const credential = GoogleAuthProvider.credentialFromResult(result)

    return { ...result, accessToken: credential?.accessToken }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null
  }
}

export const facebookAuthHandler = async () => {
  const provider = new FacebookAuthProvider()
  provider.addScope("openid")
  provider.addScope("email")

  const auth = getFirebaseAuth()

  try {
    const result = await signInWithPopup(auth, provider)
    const credential = FacebookAuthProvider.credentialFromResult(result)

    return { ...result, accessToken: credential?.accessToken }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null
  }
}
export const appleAuthHandler = async () => {
  const provider = new OAuthProvider("apple.com")
  provider.addScope("openid")
  provider.addScope("email")
  provider.addScope("name")

  const auth = getFirebaseAuth()

  try {
    const result = await signInWithPopup(auth, provider)

    // @ts-expect-error this is safe to do, because apple response contains _tokenResponse
    return { ...result, accessToken: result._tokenResponse.oauthIdToken }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null
  }
}

export const microsoftAuthHandler = async () => {
  const provider = new OAuthProvider("microsoft.com")
  provider.addScope("email")
  provider.addScope("openid")
  provider.addScope("profile")
  provider.addScope("User.Read")
  provider.setCustomParameters({ tenant: "common", prompt: "select_account" })

  const auth = getFirebaseAuth()

  try {
    const result = await signInWithPopup(auth, provider)
    const credential = OAuthProvider.credentialFromResult(result)

    return { ...result, accessToken: credential?.accessToken }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null
  }
}
